import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, Download, Loader2, X } from 'lucide-react';
import { exportRange, formatTimecode, type EditorAsset, type QuickEditorProject } from '../../../utils/quickEditor';
import { SAYINGS } from '../../../utils/sayings';
import { exportSizes, planFrames, planSound, videoBitrate, type ExportProgress, type ExportQuality } from '../../../utils/editorExportPlan';

interface ExportDialogProps {
  project: QuickEditorProject;
  /** The timeline's frame rate, from its clips: the export's to begin with. */
  frameRate: number;
  /** Each asset's bytes, once loaded. */
  media: Record<string, Blob | undefined>;
  workspaceName?: string;
  /**
   * Where the editor is. On the Edit page the dialog is the app's own: over the whole window,
   * on the blurred scrim, above everything of the page (the Nodes / Edit switch too). In a
   * node's card it covers the card only, on a plain scrim: nothing is blurred inside a card.
   */
  variant?: 'node' | 'page';
  onClose: () => void;
}

/** What Tab stops at inside the dialog. */
const TAB_STOPS = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const FRAME_RATES = [24, 25, 30, 60];
/** The usual rates, with the timeline's own among them when it is not one of them (23.976, 29.97). */
const frameRateChoices = (timeline: number): number[] =>
  (FRAME_RATES.includes(timeline) ? FRAME_RATES : [...FRAME_RATES, timeline].sort((a, b) => a - b));
const rateLabel = (rate: number): string => (Number.isInteger(rate) ? String(rate) : rate.toFixed(3).replace(/0+$/, ''));
const QUALITIES: { id: ExportQuality; label: string; hint: string }[] = [
  { id: 'standard', label: 'Standard', hint: 'Smaller file' },
  { id: 'high', label: 'High', hint: 'For sharing' },
  { id: 'best', label: 'Best', hint: 'Larger file, for further editing' },
];
const STAGE_LABEL: Record<ExportProgress['stage'], string> = {
  preparing: 'Getting ready',
  sound: 'Mixing the sound',
  picture: 'Rendering the picture',
  finishing: 'Finishing the file',
};

type Status =
  | { kind: 'setup' }
  | { kind: 'running'; progress: ExportProgress }
  | { kind: 'done'; fileName: string; sizeBytes?: number; url?: string; savedToDisk: boolean }
  | { kind: 'error'; message: string };

const formatSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const safeName = (name: string) => name.replace(/[\\/:*?"<>|]+/g, '').trim() || 'Edit';

type SaveHandle = FileSystemFileHandle & { remove?: () => Promise<void> };
type SavePicker = (options: { suggestedName: string; types: { description: string; accept: Record<string, string[]> }[] }) => Promise<SaveHandle>;

/**
 * Export the edit as an MP4: between the in and out points or all of it, at a chosen size,
 * frame rate and quality. Where the browser can, the file is written straight to a place the
 * user picks while it renders; otherwise it is downloaded when done.
 */
export const ExportDialog: React.FC<ExportDialogProps> = ({ project, frameRate, media, workspaceName, variant = 'node', onClose }) => {
  const isPage = variant === 'page';
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);
  const range = exportRange(project);
  const [useRange, setUseRange] = useState(range.marked);
  const startSec = useRange ? range.startSec : 0;
  const endSec = useRange ? range.endSec : exportRange({ ...project, inSec: undefined, outSec: undefined }).endSec;

  // The size of the main video: the first one in the part being exported
  const mainVideo = useMemo((): EditorAsset | undefined => {
    const videoTracks = new Set(project.tracks.filter(t => t.kind === 'video').map(t => t.id));
    const clips = project.clips
      .filter(c => videoTracks.has(c.trackId) && c.startSec < endSec && c.startSec + (c.outSec - c.inSec) > startSec)
      .sort((a, b) => a.startSec - b.startSec);
    for (const clip of clips) {
      const asset = project.assets.find(a => a.id === clip.assetId);
      if (asset?.width && asset.height) return asset;
    }
    return project.assets.find(a => a.kind === 'video' && a.width && a.height);
  }, [project, startSec, endSec]);
  const sizes = useMemo(() => exportSizes(mainVideo?.width ?? 1920, mainVideo?.height ?? 1080), [mainVideo]);
  const [sizeIndex, setSizeIndex] = useState(0);
  const size = sizes[Math.min(sizeIndex, sizes.length - 1)];
  const [fps, setFps] = useState(frameRate);
  const [quality, setQuality] = useState<ExportQuality>('high');
  const [status, setStatus] = useState<Status>({ kind: 'setup' });
  const abortRef = useRef<AbortController | null>(null);
  const urlRef = useRef<string | null>(null);
  useEffect(() => () => { abortRef.current?.abort(); if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const settings = { startSec, endSec, width: size.width, height: size.height, fps, quality };
  const hasPicture = useMemo(() => planFrames(project, { startSec, endSec, fps: 2 }).some(layers => layers.length > 0), [project, startSec, endSec]);
  // Media that must be loaded before exporting
  const notReady = useMemo(() => {
    const used = new Set([
      ...planFrames(project, { startSec, endSec, fps: 4 }).flat().map(l => l.assetId),
      ...planSound(project, { startSec, endSec }).map(s => s.assetId),
    ]);
    return project.assets.filter(a => used.has(a.id) && !media[a.id]).map(a => a.name);
  }, [project, media, startSec, endSec]);
  const extension = hasPicture ? 'mp4' : 'm4a';
  const baseName = safeName(`${workspaceName || 'Edit'}${useRange && range.marked ? ` ${formatTimecode(startSec)}-${formatTimecode(endSec)}`.replace(/:/g, '.') : ''}`);

  const running = status.kind === 'running';
  // The dialog has the keyboard from the moment it opens (the editor takes it back on close)
  useEffect(() => { sheetRef.current?.focus({ preventScroll: true }); }, []);
  // Esc closes the dialog, except while exporting. Over the whole window, Tab stays inside it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (running) return;
        e.stopPropagation();
        onClose();
        return;
      }
      const sheet = sheetRef.current;
      if (e.key !== 'Tab' || !isPage || !sheet) return;
      const stops = Array.from(sheet.querySelectorAll<HTMLElement>(TAB_STOPS)).filter(el => el.offsetParent !== null);
      const first = stops[0] ?? sheet;
      const last = stops[stops.length - 1] ?? sheet;
      const at = document.activeElement;
      const to = !sheet.contains(at) ? (e.shiftKey ? last : first)
        : e.shiftKey && (at === first || at === sheet) ? last
        : !e.shiftKey && at === last ? first
        : null;
      if (!to) return;
      e.preventDefault();
      to.focus();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [running, onClose, isPage]);

  const start = async () => {
    let handle: SaveHandle | null = null;
    let writable: FileSystemWritableFileStream | undefined;
    const picker = (window as unknown as { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
    if (picker) {
      try {
        handle = await picker({
          suggestedName: `${baseName}.${extension}`,
          types: [{ description: hasPicture ? 'MP4 video' : 'MPEG-4 audio', accept: { [hasPicture ? 'video/mp4' : 'audio/mp4']: [`.${extension}`] } }],
        });
        writable = await handle.createWritable();
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return; // the user closed the save window
        handle = null; // no save window here: the file is downloaded when done
      }
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus({ kind: 'running', progress: { stage: 'preparing', fraction: 0 } });
    try {
      // The encoder is loaded only now, so the editor opens without it
      const { exportEdit } = await import('../../../utils/editorExport');
      const blob = await exportEdit(project, media, settings, {
        writable: writable as unknown as WritableStream | undefined,
        signal: controller.signal,
        onProgress: (progress) => setStatus(prev => (prev.kind === 'running' ? { kind: 'running', progress } : prev)),
        assetNames: Object.fromEntries(project.assets.map(a => [a.id, a.name])),
      });
      if (controller.signal.aborted) return;
      if (blob) {
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        const fileName = `${baseName}.${extension}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setStatus({ kind: 'done', fileName, sizeBytes: blob.size, url, savedToDisk: false });
      } else {
        const file = handle ? await handle.getFile().catch(() => null) : null;
        setStatus({ kind: 'done', fileName: handle?.name || `${baseName}.${extension}`, sizeBytes: file?.size, savedToDisk: true });
      }
    } catch (e) {
      // A cancelled export leaves no half-written file behind
      if (handle?.remove) await handle.remove().catch(() => undefined);
      if ((e as Error)?.name === 'AbortError' || controller.signal.aborted) { setStatus({ kind: 'setup' }); return; }
      setStatus({ kind: 'error', message: (e as Error)?.message || 'The export failed.' });
    } finally {
      abortRef.current = null;
    }
  };

  /** One of the two ranges: a row to pick, lavender while it is the chosen one. */
  const rangeRow = (chosen: boolean) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg border ${chosen ? 'border-lavender bg-lavender-soft' : 'border-cream-300'}`;
  const percent = status.kind === 'running' ? Math.round(status.progress.fraction * 100) : 0;

  const dialog = (
    // The app's dialog: the white sheet with a header row and a body that scrolls. On the Edit page
    // it lies on the app's scrim, over the whole window. In a node's card the scrim is plain ink
    // over the card alone: ns-modal-scrim is fixed to the window and blurred, and a card has no blur.
    <div
      className={isPage ? 'ns-modal-scrim' : 'absolute inset-0 z-40 grid place-items-center bg-ink/60 p-4'}
      onPointerDown={(e) => { if (e.target === e.currentTarget && !running) onClose(); }}
      data-export-dialog
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`ns-modal-sheet ns-modal-sheet-flush text-ink focus:outline-none ${isPage ? '' : 'max-h-full'}`}
      >
        <div className="flex items-center justify-between gap-3 pl-6 pr-5 pt-5 pb-4 shrink-0 border-b border-cream-300">
          <h2 id={titleId} className="ns-t-dialog min-w-0">Export {hasPicture ? 'video' : 'audio'}</h2>
          {/* An export that is running is stopped with its own button, not by closing */}
          <button type="button" onClick={onClose} disabled={running} className="ns-icon-btn" aria-label="Close" title={running ? 'Cancel the export to close' : undefined}><X /></button>
        </div>

        {status.kind === 'setup' || status.kind === 'error' ? (
          <div className="min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-4">
            <fieldset className="flex flex-col gap-1.5">
              <legend className="ns-t-label mb-1.5">Range</legend>
              <label className={`${rangeRow(useRange)} ${range.marked ? 'cursor-pointer' : 'opacity-50'}`}>
                <input type="radio" name="export-range" checked={useRange} disabled={!range.marked} onChange={() => setUseRange(true)} className="accent-lavender-deep" />
                <span className="flex-1 text-[13px]">
                  <span className="font-semibold">In to out</span>
                  <span className="block text-xs text-ink-soft">
                    {range.marked ? `${formatTimecode(range.startSec)} to ${formatTimecode(range.endSec)} · ${(range.endSec - range.startSec).toFixed(1)} s` : 'Mark in and out points on the timeline (I and O)'}
                  </span>
                </span>
              </label>
              <label className={`${rangeRow(!useRange)} cursor-pointer`}>
                <input type="radio" name="export-range" checked={!useRange} onChange={() => setUseRange(false)} className="accent-lavender-deep" />
                <span className="flex-1 text-[13px]">
                  <span className="font-semibold">Whole edit</span>
                  <span className="block text-xs text-ink-soft">{formatTimecode(0)} to {formatTimecode(useRange ? exportRange({ ...project, inSec: undefined, outSec: undefined }).endSec : endSec)}</span>
                </span>
              </label>
            </fieldset>

            {hasPicture && (
              <>
                <div className="flex items-center gap-3">
                  <label htmlFor="export-size" className="ns-t-label w-20 shrink-0">Size</label>
                  <select
                    id="export-size"
                    value={Math.min(sizeIndex, sizes.length - 1)}
                    onChange={(e) => setSizeIndex(Number(e.target.value))}
                    className="ns-field ns-field-sm flex-1"
                    data-export-size
                  >
                    {sizes.map((option, i) => <option key={option.label} value={i}>{option.label} · {option.width} × {option.height}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-3">
                  <span className="ns-t-label w-20 shrink-0">Frame rate</span>
                  <div className="ns-seg" role="radiogroup" aria-label="Frame rate">
                    {frameRateChoices(frameRate).map(rate => (
                      <button key={rate} type="button" role="radio" aria-checked={fps === rate} onClick={() => setFps(rate)} className="ns-seg-item tabular-nums" data-export-fps={rate} title={rate === frameRate ? 'The frame rate of your clips' : undefined}>{rateLabel(rate)}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div className="flex items-start gap-3">
              <span className="ns-t-label w-20 shrink-0 pt-2">Quality</span>
              <div className="flex-1 min-w-0">
                <div className="ns-seg" role="radiogroup" aria-label="Quality">
                  {QUALITIES.map(option => (
                    <button key={option.id} type="button" role="radio" aria-checked={quality === option.id} onClick={() => setQuality(option.id)} className="ns-seg-item" data-export-quality={option.id}>{option.label}</button>
                  ))}
                </div>
                <p className="ns-t-small mt-1.5">{QUALITIES.find(q => q.id === quality)?.hint} · {hasPicture ? `H.264 at about ${Math.round(videoBitrate(settings) / 100_000) / 10} Mbps, AAC sound` : 'AAC sound'}</p>
              </div>
            </div>

            {notReady.length > 0 && (
              <p className="ns-note" data-tone="warn">
                <AlertTriangle size={14} aria-hidden="true" />
                <span>Still loading: {notReady.join(', ')}. Export once every clip in the range is ready.</span>
              </p>
            )}
            {status.kind === 'error' && (
              <p className="ns-note" data-tone="danger" role="alert">
                <AlertTriangle size={14} aria-hidden="true" />
                <span>{status.message}</span>
              </p>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="ns-btn ns-btn-sm ns-btn-soft">Cancel</button>
              <button
                type="button"
                onClick={() => void start()}
                disabled={notReady.length > 0 || endSec - startSec <= 0}
                className="ns-btn ns-btn-sm ns-btn-primary"
                data-export-start
              >
                <Download size={14} /> Export
              </button>
            </div>
          </div>
        ) : status.kind === 'running' ? (
          <div className="min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-3" data-export-running>
            <div className="flex items-center gap-2 ns-t-title">
              <Loader2 size={14} className="animate-spin text-lavender-deep" aria-hidden="true" />
              <span className="flex-1">{STAGE_LABEL[status.progress.stage]}</span>
              <span className="tabular-nums font-medium text-ink-soft" data-export-percent>{percent}%</span>
            </div>
            <div className="ns-meter" role="progressbar" aria-label="Export progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
              <div style={{ width: `${status.progress.fraction * 100}%` }} />
            </div>
            <p className="ns-t-small">
              {formatTimecode(endSec - startSec)} at {size.width} × {size.height}, {rateLabel(fps)} fps. Keep this tab open until it finishes.
            </p>
            <div className="flex justify-end pt-1">
              <button type="button" onClick={() => abortRef.current?.abort()} className="ns-btn ns-btn-sm ns-btn-soft" data-export-cancel>Cancel export</button>
            </div>
          </div>
        ) : (
          <div className="min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-3" data-export-done>
            {/* The export's saying (utils/sayings.ts): the one place it is said */}
            <div className="flex items-center gap-3">
              <img src={SAYINGS.done.picture} alt={SAYINGS.done.alt} className="w-14 h-14 shrink-0 rounded-xl object-cover" decoding="async" draggable={false} />
              <p className="text-xl font-bold tracking-[-0.02em] text-ink">{SAYINGS.done.line}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="ns-mark ns-mark-sm" data-tone="ok"><Check aria-hidden="true" /></span>
              <div className="min-w-0">
                <p className="ns-t-title truncate" title={status.fileName}>{status.fileName}</p>
                <p className="ns-t-small">
                  {[status.sizeBytes ? formatSize(status.sizeBytes) : '', status.savedToDisk ? 'Saved where you chose' : 'Downloaded'].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              {status.url && (
                <a href={status.url} download={status.fileName} className="ns-btn ns-btn-sm ns-btn-ghost">
                  <Download size={14} /> Download again
                </a>
              )}
              <button type="button" onClick={onClose} className="ns-btn ns-btn-sm ns-btn-primary">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Out of the Edit page's own layer and into the window's, or the page switch would lie over the scrim
  return isPage ? createPortal(dialog, document.body) : dialog;
};

export default ExportDialog;
